import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { extractEventIdsFromPaths } from "@/lib/analytics";
import { displayAdminEventLabel } from "@/lib/datetime";

/**
 * Hook to batch fetch event titles for a list of paths
 * Returns a map of eventId -> title
 */
export const useEventTitles = (paths: string[]) => {
  const eventIds = extractEventIdsFromPaths(paths);

  return useQuery({
    queryKey: ["event-titles", eventIds.sort().join(",")],
    queryFn: async (): Promise<Record<string, string>> => {
      if (eventIds.length === 0) return {};

      const { data, error } = await supabase
        .from("events")
        .select("id, title, start_time, timezone")
        .in("id", eventIds);

      if (error) throw error;

      return Object.fromEntries(
        (data || []).map((event) => [
          event.id,
          displayAdminEventLabel(event.title, event.start_time, event.timezone || undefined),
        ]),
      );
    },
    enabled: eventIds.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};
